import * as moment from 'moment';

import {
	BulkAssignShiftsResponse,
	FieldWorker,
	JobShift,
	ServiceType,
} from 'src/shared/api';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { JobShiftActionTypes, JobShiftActions } from './job-shift.actions';

export interface JobShiftStoreModel extends JobShift {
	originalRwic?: FieldWorker;
	dirty?: boolean;
}

export interface ShiftByStartTime {
	id: number;
	startTime?: string | moment.Moment;
	serviceType?: ServiceType;
	hourShifts?: ShiftByWeekday[];
}
export interface ShiftByWeekday {
	weekday: number;
	shiftUnAssignedCount?: number;
	shiftsNeeded: JobShiftStoreModel[];
	needsAssignments: boolean;
	allAssigned: boolean;
	isDuringJob: boolean;
	isStartOfJob: boolean;
	isEndOfJob: boolean;
	continuesAfter: boolean;
	continuesBefore: boolean;
}
export const jobShiftsFeatureKey = 'jobShift';

export interface JobShiftState extends EntityState<JobShiftStoreModel> {
	loading: boolean;
	startDate: moment.Moment;
	shiftsToDelete: JobShiftStoreModel[];
	shiftIdsToDelete: number[];
	bulkAssignShiftsResponse: BulkAssignShiftsResponse[];
	// additional entities state properties
}

export const adapter: EntityAdapter<JobShiftStoreModel> = createEntityAdapter<
	JobShiftStoreModel
>();

export const initialState: JobShiftState = adapter.getInitialState({
	loading: false,
	startDate: moment.utc().startOf('w'),
	shiftsToDelete: [],
	shiftIdsToDelete: [],
	bulkAssignShiftsResponse: [],
	// additional entity state properties
});

export function reducer(
	state = initialState,
	action: JobShiftActions
): JobShiftState {
	switch (action.type) {
		case JobShiftActionTypes.JobShiftsRequested:
			return {
				...state,
				startDate: action.payload.startDate,
				loading: true,
			};

		case JobShiftActionTypes.AddJobShift: {
			return adapter.addOne(action.payload.jobShift, {
				...state,
				loading: false,
			});
		}

		case JobShiftActionTypes.AssignmentsAddJobShift: {
			return adapter.addOne(action.payload.jobShift, {
				...state,
				loading: false,
			});
		}

		case JobShiftActionTypes.RefreshJobShiftsRequested: {
			return { ...state, loading: true };
		}

		case JobShiftActionTypes.AddJobShifts: {
			return adapter.addMany(action.payload.jobShifts, {
				...state,
				loading: false,
			});
		}

		case JobShiftActionTypes.UpdateJobShift: {
			return adapter.updateOne(action.payload.jobShift, {
				...state,
				loading: false,
			});
		}

		case JobShiftActionTypes.UpdateJobShifts: {
			return adapter.updateMany(action.payload.jobShifts, {
				...state,
				loading: false,
			});
		}

		case JobShiftActionTypes.DeleteJobShift: {
			return adapter.removeOne(action.payload.id, state);
		}

		case JobShiftActionTypes.ClearShiftDeletions: {
			return {
				...state,
				shiftsToDelete: [],
				shiftIdsToDelete: []
			}
		}

		case JobShiftActionTypes.AssignmentsDeleteJobShift: {
			return {
				...state,
				shiftsToDelete: [...state.shiftsToDelete, action.payload.shift],
				shiftIdsToDelete: [
					...state.shiftIdsToDelete,
					+action.payload.shift.id,
				],
			};
		}

		case JobShiftActionTypes.BulkAddShiftsRequested: {
			return { ...state, loading: true };
		}
		case JobShiftActionTypes.BulkAddShiftsSaved: {
			return { ...state, loading: false };
		}
		case JobShiftActionTypes.BulkAssignShiftsRequested: {
			return { ...state, bulkAssignShiftsResponse: null, loading: true };
		}
		case JobShiftActionTypes.BulkAssignShiftsError: {
			return { ...state, loading: false };
		}

		case JobShiftActionTypes.SetBulkAssignShifts:
			return {
				...state,
				bulkAssignShiftsResponse:
					action.payload.bulkAssignShiftsResponse,
				loading: false,
			};

		case JobShiftActionTypes.DeleteJobShifts: {
			return adapter.removeMany(action.payload.ids, state);
		}

		case JobShiftActionTypes.JobAssignmentsClearJobShifts: {
			return adapter.removeAll(state);
		}

		case JobShiftActionTypes.JobDetailClearJobShifts: {
			return adapter.removeAll(state);
		}

		case JobShiftActionTypes.UpdateStartDate: {
			return {
				...state,
				startDate: action.payload.startDate,
				loading: false,
			};
		}

		default: {
			return state;
		}
	}
}

export const {
	selectIds,
	selectEntities,
	selectAll,
	selectTotal,
} = adapter.getSelectors();
